import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, GripVertical, Plus, Save, Send, Trash2 } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { toast } from '@clareefai/ui';
import type { SurveyQuestion, SurveyQuestionType } from '@clareefai/types';
import { useGetSurveyQuery, useUpdateSurveyQuestionsMutation } from '../../store/surveyApi';

const TYPE_LABEL_KEYS: Record<SurveyQuestionType, string> = {
  NPS_SCALE: 'surveys.questions.npsScaleLabel',
  FREE_TEXT: 'surveys.questions.freeTextLabel',
};

const SurveyQuestions: React.FC = () => {
  const { t } = useTranslation();
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();

  const { data: survey, isLoading } = useGetSurveyQuery(surveyId!, { skip: !surveyId });
  const [updateQuestions, { isLoading: isSaving }] = useUpdateSurveyQuestionsMutation();

  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (survey?.questions?.length) {
      setQuestions([...survey.questions].sort((a, b) => a.order - b.order));
    }
  }, [survey?.id]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = Array.from(questions);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setQuestions(reordered.map((q, i) => ({ ...q, order: i })));
  };

  const handleTextChange = (id: string, text: string) => {
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, text } : q)));
  };

  const handleTypeChange = (id: string, type: SurveyQuestionType) => {
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, type } : q)));
  };

  const handleDelete = (id: string) => {
    setQuestions((prev) => prev.filter((q) => q.id !== id).map((q, i) => ({ ...q, order: i })));
  };

  const handleAddQuestion = () => {
    const newQ: SurveyQuestion = {
      id: `new-${Date.now()}`,
      text: '',
      type: 'NPS_SCALE',
      required: true,
      order: questions.length,
    };
    setQuestions((prev) => [...prev, newQ]);
  };

  const handleSave = async () => {
    if (!surveyId) return;
    const empty = questions.find((q) => !q.text.trim());
    if (empty) {
      toast.error(t('surveys.questions.allQuestionsMustHaveText'));
      return;
    }
    try {
      await updateQuestions({
        surveyId,
        data: {
          questions: questions.map((q, i) => ({
            id: q.id.startsWith('new-') ? undefined : q.id,
            text: q.text,
            type: q.type,
            required: q.required,
            order: i,
          })),
        },
      }).unwrap();
      toast.success(t('surveys.questions.questionsSaved'));
      setSaved(true);
    } catch {
      toast.error(t('surveys.questions.failedToSave'));
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/survey/${surveyId}`)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
        </button>
        <div>
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-purple-600" />
            <h1 className="text-xl font-bold text-gray-900">{t('surveys.questions.title')}</h1>
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            {survey?.title ? t('surveys.questions.editingTitle', { title: survey.title }) : t('surveys.questions.defaultSubtitle')}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-36 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="questions">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                  {questions.map((question, index) => (
                    <Draggable key={question.id} draggableId={question.id} index={index}>
                      {(drag, snapshot) => (
                        <div
                          ref={drag.innerRef}
                          {...drag.draggableProps}
                          className={`bg-white rounded-xl border p-4 space-y-3 transition-shadow ${
                            snapshot.isDragging ? 'border-purple-300 shadow-lg' : 'border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div {...drag.dragHandleProps} className="cursor-grab text-gray-300 hover:text-gray-500">
                              <GripVertical className="w-4 h-4" />
                            </div>
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0">
                              {t('surveys.questions.questionLabel', { index: index + 1 })}
                            </span>
                            {question.type === 'FREE_TEXT' && (
                              <span className="ml-auto text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
                                {t('surveys.questions.generalFeedback')} <span className="text-red-500">{t('surveys.questions.required')}</span> *
                              </span>
                            )}
                            <button
                              onClick={() => handleDelete(question.id)}
                              className="ml-auto p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          <input
                            value={question.text}
                            onChange={(e) => handleTextChange(question.id, e.target.value)}
                            placeholder={t('surveys.questions.enterText')}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />

                          <div className="flex gap-3">
                            {(['NPS_SCALE', 'FREE_TEXT'] as SurveyQuestionType[]).map((type) => (
                              <button
                                key={type}
                                onClick={() => handleTypeChange(question.id, type)}
                                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium border transition-colors ${
                                  question.type === type
                                    ? 'bg-purple-50 border-purple-300 text-purple-700'
                                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                              >
                                {t(TYPE_LABEL_KEYS[type])}
                              </button>
                            ))}
                          </div>

                          {question.type === 'NPS_SCALE' && (
                            <div className="space-y-1">
                              <p className="text-xs text-gray-400">
                                {t('surveys.questions.npsHelp')}
                              </p>
                              <div className="flex gap-1 flex-wrap">
                                {[...Array(11)].map((_, n) => (
                                  <span
                                    key={n}
                                    className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-lg text-xs text-gray-500"
                                  >
                                    {n}
                                  </span>
                                ))}
                              </div>
                              <div className="flex justify-between text-xs text-gray-400">
                                <span>{t('surveys.questions.notAtAllLikely')}</span>
                                <span>{t('surveys.questions.extremelyLikely')}</span>
                              </div>
                            </div>
                          )}

                          {question.type === 'FREE_TEXT' && (
                            <p className="text-xs text-gray-400 italic bg-gray-50 rounded-lg p-2">
                              {t('surveys.questions.freeTextHelp')}
                            </p>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <button
            onClick={handleAddQuestion}
            className="w-full py-3 border-2 border-dashed border-purple-200 text-purple-600 rounded-xl text-sm font-medium hover:border-purple-400 hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('surveys.questions.addQuestion')}
          </button>

          {saved && (
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">{t('surveys.questions.savedSuccessfully')}</p>
                <p className="text-xs text-green-600 mt-0.5">{t('surveys.questions.readyToSend')}</p>
              </div>
              <button
                onClick={() => navigate(`/survey/send/${surveyId}`)}
                className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex-shrink-0"
              >
                <Send className="w-4 h-4" />
                {t('surveys.detail.inviteParticipants')}
              </button>
            </div>
          )}

          <div className="flex justify-between">
            <button
              onClick={() => navigate(`/survey/${surveyId}`)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('surveys.questions.backToSurvey')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg font-medium text-sm hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isSaving ? t('surveys.questions.saving') : t('surveys.questions.saveQuestions')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SurveyQuestions;
